import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useCurrentuser } from '@uplift/contexts';
import { trackStartSessionClick, useL10n } from '@uplift/hooks';
import { CalendarDateService, DateObject, formatTimeRangeWithMeridiem } from '@uplift/services';
import { ProviderMedicalRole, UserRole } from '@uplift/types';
import { DisplayName } from '@uplift/utils';

import { Button } from '../../../Button';

export type PatientSingleSession = {
  id: string;
  start: DateObject;
  end: DateObject;
  isMaterialized: boolean;
  providerName: string;
  providerRole: ProviderMedicalRole;
  providerDisplay: DisplayName;
  connectLink?: string;
  seriesId?: string;
  type: 'single';
};


export type PatientSingleSessionItemProps = {
  item: PatientSingleSession;
  onCancel: (session: { id: string }) => void;
};


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3, 3),
    boxSizing: 'border-box',

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3, 4),
      flexDirection: 'row',
      width: '100%',
    }
  },
  content: {
    width: '100%',
  },
  timeTitle: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
  },
  time: {
    marginTop: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'row-reverse',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'column',
      width: theme.spacing(17),
    }
  },
}));


export const getAppointmentTitle = (medicalRole: ProviderMedicalRole, duration: number) => {
  if (medicalRole === ProviderMedicalRole.psychiatrist) {
    if (duration === 60) {
      return 'Psychiatric Consultation';
    }
    return 'Medication Management';
  }

  return 'Psychotherapy';
};


export const PatientSingleSessionItem: React.FC<PatientSingleSessionItemProps> = ({ item, onCancel }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const me = useCurrentuser();

  const start = CalendarDateService.useDateObject(item.start);
  const end = CalendarDateService.useDateObject(item.end);

  const subtitle = React.useMemo(() => {
    const duration = Math.abs(start.diff(end, 'minutes'));

    return `${getAppointmentTitle(item.providerRole, duration)} with ${item.providerName}`;
  }, [ item, start, end ]);

  const timeRange = React.useMemo(() => {
    return formatTimeRangeWithMeridiem(start, end);
  }, [ start, end ]);

  const handleStartSession = () => {
    trackStartSessionClick({ userType: UserRole[me?.role as keyof typeof UserRole] });
  };

  return (
    <Paper elevation={1}>
      <Box className={classes.root}>
        <Box className={classes.content}>
          <Typography variant="body1" className={classes.timeTitle}>{start.format('dddd, MMM D')}</Typography>
          <Typography variant="body1" className={classes.subtitle}>{subtitle}</Typography>
          <Typography variant="body1" className={classes.time}>{timeRange}</Typography>
        </Box>
        <Box className={classes.actions}>
          <Button
            component={Link}
            to={item.connectLink}
            disabled={!item.connectLink}
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={<VideocamOutlinedIcon />}
            onClick={handleStartSession}
          >
            Connect
          </Button>
          <Button
            onClick={() => onCancel({ id: item.id })}
            fullWidth
            variant="outlined"
          >
            {l10n.general.cancel}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
